import {
  AGENT_AUD_MISMATCH_MESSAGE,
  AGENT_INEFFECTUAL_TOKEN_MESSAGE,
  AGENT_INVALID_BEARER_MESSAGE,
  AGENT_MISSING_TOKEN_MESSAGE,
  AGENT_TOKEN_EXPIRED_MESSAGE,
  isAgentTokenNearExpiry,
  isRefreshableAgentTokenError,
} from "@/clients/open_api_injection";
import { env } from "@/constants/env";
import type { AgentChatImage } from "@/clients/models/AgentChatImage";
import type { AgentChatRequest } from "@/clients/models/AgentChatRequest";
import type { ChatMessageImage } from "@/stores/chat.store";
import { decodeJWT } from "@/utils/jwt";
import type { AgentFrame, AgentWarmupRequest } from "./agent-types";
import { parseSseFrames } from "./sse-parser";
import { captureMessage } from "@sentry/react";

const AGENT_JWT_TEMPLATE = "nutricoach-agent";
const ERROR_BODY_PREVIEW_LIMIT = 2048;

// AgentCore microVMs cold-start; the runtime fails at stream-open time before
// any SSE frame is sent. This is transient — a second attempt a moment later
// almost always succeeds — and safe to retry because nothing has been streamed
// or rendered yet. We retry once after a short delay.
//
// IMPORTANT: the gateway (CloudFront/AgentCore) does NOT surface the runtime
// failure as an HTTP 502. It wraps it in an HTTP 424 ("Failed Dependency")
// envelope and puts the real error in the body:
//   { "message": "Received error (502) from runtime. ..." }
// So we key the retry on either the known transient envelope statuses OR the
// runtime-error body marker (the definitive signal across the gateway wrap).
const RETRYABLE_STREAM_STATUSES = new Set([424, 502, 503, 504]);
const RUNTIME_ERROR_BODY_RE = /Received error \(\d+\) from runtime/i;
const STREAM_RETRY_DELAY_MS = 1000;

const isRetryableRuntimeError = (status: number, bodyText: string): boolean =>
  RETRYABLE_STREAM_STATUSES.has(status) || RUNTIME_ERROR_BODY_RE.test(bodyText);

// Ongoing observability for unrecognised AgentCore failure modes. POSTs the
// full response (headers + body + decoded token claims, but NOT the raw
// bearer) to /api/v1/debug/agent-error for S3 storage. Only fires when
// `agentErrorCategory === "unknown_401"` — known categories are already
// classified and don't need raw capture. Fetch is fire-and-forget with
// keepalive so it survives page transitions.
const reportAgentDebugError = (payload: Record<string, unknown>): void => {
  try {
    void fetch(`${env.VITE_API_URL}/api/v1/debug/agent-error`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": env.VITE_API_KEY,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* swallow — debug telemetry must never break the error path */
  }
};

// Local Clerk surface — avoids touching globals while the rest of the
// codebase still accesses window.Clerk in an untyped way.
type ClerkWindow = Window & {
  Clerk?: {
    session?: {
      getToken: (opts: { template: string; skipCache?: boolean }) => Promise<string | null>;
    };
  };
};

export interface AgentTokenClaims {
  exp?: number;
  iat?: number;
  iss?: string;
  aud?: string | string[];
  sub?: string;
}

export type AgentErrorCategory =
  | "token_expired"
  | "ineffectual_token"
  | "aud_mismatch"
  | "missing_token"
  | "invalid_bearer"
  | "unknown_401"
  | "non_401";

// Coarse classification of a failed AgentCore HTTP response. Emitted as a
// Sentry tag so we can diagnose 401 reasons even when the body / message
// extras get scrubbed by `@password:filter` for matching credential-shaped
// substrings (e.g. JWTs echoed in the error body).
export const classifyAgentHttpError = (status: number, parsed: unknown): AgentErrorCategory => {
  if (status !== 401) return "non_401";
  const message = (parsed as { error?: { message?: unknown } } | null)?.error?.message;
  if (message === AGENT_TOKEN_EXPIRED_MESSAGE) return "token_expired";
  if (message === AGENT_INEFFECTUAL_TOKEN_MESSAGE) return "ineffectual_token";
  if (message === AGENT_AUD_MISMATCH_MESSAGE) return "aud_mismatch";
  if (message === AGENT_MISSING_TOKEN_MESSAGE) return "missing_token";
  if (message === AGENT_INVALID_BEARER_MESSAGE) return "invalid_bearer";
  return "unknown_401";
};

export class AgentStreamHttpError extends Error {
  status: number;
  bodyText?: string;
  agentErrorMessage?: string;
  agentErrorCategory?: AgentErrorCategory;
  tokenClaims?: AgentTokenClaims;
  didRetry?: boolean;
  constructor(
    status: number,
    message: string,
    init?: {
      bodyText?: string;
      agentErrorMessage?: string;
      agentErrorCategory?: AgentErrorCategory;
      tokenClaims?: AgentTokenClaims;
      didRetry?: boolean;
    },
  ) {
    super(message);
    this.name = "AgentStreamHttpError";
    this.status = status;
    this.bodyText = init?.bodyText;
    this.agentErrorMessage = init?.agentErrorMessage;
    this.agentErrorCategory = init?.agentErrorCategory;
    this.tokenClaims = init?.tokenClaims;
    this.didRetry = init?.didRetry;
  }
}

export class AgentStreamPrematureCloseError extends Error {
  tokenClaims?: AgentTokenClaims;
  constructor(tokenClaims?: AgentTokenClaims) {
    super("Agent stream closed before a terminal frame");
    this.name = "AgentStreamPrematureCloseError";
    this.tokenClaims = tokenClaims;
  }
}

const isTerminalFrame = (frame: AgentFrame): boolean =>
  frame.type === "done" || frame.type === "interrupt" || frame.type === "error";

const getAgentToken = async (forceRefresh = false): Promise<string> => {
  const clerkWindow = window as ClerkWindow;
  const session = clerkWindow.Clerk?.session;
  if (!session) {
    throw new Error("Clerk session not available");
  }
  if (forceRefresh) {
    const token = await session.getToken({ template: AGENT_JWT_TEMPLATE, skipCache: true });
    if (!token) throw new Error("Failed to mint agent JWT");
    return token;
  }
  const cached = await session.getToken({ template: AGENT_JWT_TEMPLATE, skipCache: false });
  if (!cached) throw new Error("Failed to mint agent JWT");
  if (!isAgentTokenNearExpiry(cached)) return cached;
  const refreshed = await session.getToken({ template: AGENT_JWT_TEMPLATE, skipCache: true });
  return refreshed ?? cached;
};

// Decode only the public claims that help diagnose AgentCore inbound auth
// rejections. Signature, custom claims, and PII are intentionally excluded.
const extractTokenClaims = (token: string): AgentTokenClaims | undefined => {
  const decoded = decodeJWT(token);
  if (!decoded || typeof decoded !== "object") return undefined;
  const { exp, iat, iss, aud, sub } = decoded as Record<string, unknown>;
  return {
    exp: typeof exp === "number" ? exp : undefined,
    iat: typeof iat === "number" ? iat : undefined,
    iss: typeof iss === "string" ? iss : undefined,
    aud: typeof aud === "string" || Array.isArray(aud) ? (aud as string | string[]) : undefined,
    sub: typeof sub === "string" ? sub : undefined,
  };
};

const parseAgentErrorBody = (bodyText: string): { agentErrorMessage?: string; parsed: unknown } => {
  try {
    const parsed = JSON.parse(bodyText);
    const message = (parsed as { error?: { message?: unknown } } | null)?.error?.message;
    return {
      agentErrorMessage: typeof message === "string" ? message : undefined,
      parsed,
    };
  } catch {
    return { parsed: undefined };
  }
};

interface StreamAgentChatParams {
  prompt: string;
  sessionId: string;
  clientTurnId: string;
  images?: ChatMessageImage[];
  signal?: AbortSignal;
}

interface OpenStreamResult {
  response: Response;
  token: string;
  didRetry: boolean;
}

const postChat = async (
  body: AgentChatRequest,
  sessionId: string,
  signal: AbortSignal | undefined,
  forceRefresh: boolean,
): Promise<{ response: Response; token: string }> => {
  const token = await getAgentToken(forceRefresh);
  const response = await fetch(`${env.VITE_AGENT_API_URL}/chat`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      "X-Amzn-Bedrock-AgentCore-Runtime-Session-Id": sessionId,
    },
    body: JSON.stringify(body),
    signal,
  });
  return { response, token };
};

// Resolves after `ms`, but rejects with an AbortError the moment `signal`
// aborts — so a user cancelling during the retry wait is honored and we don't
// fire a doomed retry afterwards. runTurn's catch already treats AbortError as
// a silent cancel.
const abortableDelay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException("Aborted", "AbortError"));
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });

// Emit once after the retry outcome is known. Opening the stream does not prove
// that the reply completed. Cancellation is separate from an exhausted retry;
// it must not appear in the recovered=false failure count. Retain the existing
// message and recovered tag for dashboards, with outcome clarifying the scope.
const reportRuntimeRetryableError = (
  status: number,
  outcome: "stream_opened" | "retry_failed" | "cancelled",
  sessionId: string,
): void => {
  captureMessage("AI chat runtime 502", {
    level: "warning",
    tags: {
      feature: "ai-chat",
      phase: "stream",
      httpStatus: String(status),
      outcome,
      ...(outcome === "cancelled" ? {} : { recovered: String(outcome === "stream_opened") }),
    },
    extra: { sessionId },
  });
};

// Open the chat stream, retrying once with a force-refreshed templated token
// if AgentCore rejects the inbound JWT as refreshable ("Token has expired" or
// "Ineffectual token, will expire within the next minute"). Mirrors the REST
// recovery in src/clients/open_api_injection.ts. Audience mismatches and
// other 401 reasons surface unchanged.
const openAgentStream = async (
  body: AgentChatRequest,
  sessionId: string,
  signal: AbortSignal | undefined,
): Promise<OpenStreamResult> => {
  const first = await postChat(body, sessionId, signal, false);
  if (first.response.ok) {
    return { response: first.response, token: first.token, didRetry: false };
  }
  if (first.response.status !== 401) {
    // Read the body to detect the runtime-error marker. Clone first so the
    // untruncated read in buildHttpError (the non-retryable path below) still
    // has an unconsumed body to work with.
    const firstBodyText = await readBodyPreview(first.response.clone());
    // Transient runtime cold-start: retry once after a short delay. Token is
    // fine — only the runtime was cold — so no force-refresh, deliberately
    // distinct from the 401 path below.
    if (isRetryableRuntimeError(first.response.status, firstBodyText)) {
      let retry: Awaited<ReturnType<typeof postChat>>;
      try {
        await abortableDelay(STREAM_RETRY_DELAY_MS, signal);
        retry = await postChat(body, sessionId, signal, false);
        if (!retry.response.ok) {
          throw await buildHttpError(retry.response, retry.token, true);
        }
      } catch (error) {
        const cancelled =
          signal?.aborted ||
          (typeof error === "object" &&
            error !== null &&
            "name" in error &&
            error.name === "AbortError");
        reportRuntimeRetryableError(
          first.response.status,
          cancelled ? "cancelled" : "retry_failed",
          sessionId,
        );
        throw error;
      }
      reportRuntimeRetryableError(first.response.status, "stream_opened", sessionId);
      return { response: retry.response, token: retry.token, didRetry: true };
    }
    throw await buildHttpError(first.response, first.token, false);
  }

  // Clone before consuming so the untruncated debug body read is independent
  // of the readBodyPreview/parse path used for the thrown error.
  const debugClone = first.response.clone();
  const bodyText = await readBodyPreview(first.response);
  const { agentErrorMessage, parsed } = parseAgentErrorBody(bodyText);
  if (!isRefreshableAgentTokenError(parsed)) {
    const agentErrorCategory = classifyAgentHttpError(first.response.status, parsed);
    const debugPayload = {
      site: "openAgentStream:not_refreshable",
      url: first.response.url,
      status: first.response.status,
      statusText: first.response.statusText,
      headers: Object.fromEntries(first.response.headers.entries()),
      body: await debugClone.text().catch(() => bodyText),
      bodyText,
      agentErrorMessage,
      agentErrorCategory,
      tokenClaims: extractTokenClaims(first.token),
      sessionId,
      clientNow: Date.now(),
      userAgent: navigator.userAgent,
    };
    console.error("[agent-stream debug] 401 (not refreshable)", debugPayload);
    if (agentErrorCategory === "unknown_401") reportAgentDebugError(debugPayload);
    throw new AgentStreamHttpError(first.response.status, `Agent stream HTTP 401`, {
      bodyText,
      agentErrorMessage,
      agentErrorCategory,
      tokenClaims: extractTokenClaims(first.token),
      didRetry: false,
    });
  }

  const second = await postChat(body, sessionId, signal, true);
  if (second.response.ok) {
    return { response: second.response, token: second.token, didRetry: true };
  }
  throw await buildHttpError(second.response, second.token, true);
};

const readBodyPreview = async (response: Response): Promise<string> => {
  try {
    const text = await response.text();
    return text.length > ERROR_BODY_PREVIEW_LIMIT ? text.slice(0, ERROR_BODY_PREVIEW_LIMIT) : text;
  } catch {
    return "";
  }
};

const buildHttpError = async (
  response: Response,
  token: string,
  didRetry: boolean,
): Promise<AgentStreamHttpError> => {
  // Clone before consuming so the untruncated debug body read is independent
  // of the readBodyPreview/parse path used for the thrown error.
  const debugClone = response.clone();
  const bodyText = await readBodyPreview(response);
  const { agentErrorMessage, parsed } = parseAgentErrorBody(bodyText);
  const agentErrorCategory = classifyAgentHttpError(response.status, parsed);
  const debugPayload = {
    site: "buildHttpError",
    url: response.url,
    status: response.status,
    statusText: response.statusText,
    didRetry,
    headers: Object.fromEntries(response.headers.entries()),
    body: await debugClone.text().catch(() => bodyText),
    bodyText,
    agentErrorMessage,
    agentErrorCategory,
    tokenClaims: extractTokenClaims(token),
    clientNow: Date.now(),
    userAgent: navigator.userAgent,
  };
  console.error("[agent-stream debug] HTTP error", debugPayload);
  if (agentErrorCategory === "unknown_401") reportAgentDebugError(debugPayload);
  return new AgentStreamHttpError(response.status, `Agent stream HTTP ${response.status}`, {
    bodyText,
    agentErrorMessage,
    agentErrorCategory,
    tokenClaims: extractTokenClaims(token),
    didRetry,
  });
};

export async function* streamAgentChat({
  prompt,
  sessionId,
  clientTurnId,
  images = [],
  signal,
}: StreamAgentChatParams): AsyncGenerator<AgentFrame> {
  const body: AgentChatRequest & { client_turn_id: string } = images.length
    ? {
        prompt,
        client_turn_id: clientTurnId,
        images: images.map(
          (image): AgentChatImage => ({
            data: image.dataUrl,
            mime_type: image.mimeType as AgentChatImage.mime_type,
          }),
        ),
      }
    : { prompt, client_turn_id: clientTurnId };

  const { response, token } = await openAgentStream(body, sessionId, signal);
  if (!response.body) {
    throw new AgentStreamHttpError(response.status, `Agent stream missing body`, {
      agentErrorCategory: "non_401",
      tokenClaims: extractTokenClaims(token),
    });
  }

  let sawTerminal = false;
  for await (const frame of parseSseFrames(response.body, signal)) {
    yield frame;
    if (isTerminalFrame(frame)) {
      sawTerminal = true;
      break;
    }
  }
  if (!sawTerminal && !signal?.aborted) {
    throw new AgentStreamPrematureCloseError(extractTokenClaims(token));
  }
}

// Time-based dedupe. AgentCore microVMs idle out within a few minutes, and
// warmUpAgentSession now fires on every native app-open surface (cold boot
// and OS resume), so a forever-Set would block legitimate re-warms after a
// multi-minute background. WARMUP_DEDUPE_MS sits comfortably below the VM
// idle TTL so any foreground after that window re-warms, while still
// collapsing rapid double-fires (strict-mode mounts, resume + chat-page mount
// landing together) into a single request.
const WARMUP_DEDUPE_MS = 3 * 60 * 1000;
const WARMUP_TIMEOUT_MS = 15_000;
const lastWarmedAt = new Map<string, number>();
const pendingWarmups = new Map<string, AbortController>();

// Fire-and-forget warm-up ping. POSTs {"type":"warmup"} to the same /chat
// endpoint with the session header so AgentCore provisions the microVM the
// user's first real message will land on. Only a successful HTTP response starts
// the freshness window. Failed attempts remain eligible on the next trigger;
// pending attempts are shared and bounded, without blocking a user's message.
// Failures remain silent: normal chat retains its own retry path.
export const warmUpAgentSession = (sessionId: string): void => {
  const previous = lastWarmedAt.get(sessionId);
  if (previous !== undefined && Date.now() - previous < WARMUP_DEDUPE_MS) return;
  if (pendingWarmups.has(sessionId)) return;
  const controller = new AbortController();
  pendingWarmups.set(sessionId, controller);
  const timeout = setTimeout(() => {
    controller.abort();
    if (pendingWarmups.get(sessionId) === controller) pendingWarmups.delete(sessionId);
  }, WARMUP_TIMEOUT_MS);

  void (async () => {
    try {
      const token = await getAgentToken();
      if (!token || controller.signal.aborted) return;
      const body: AgentWarmupRequest = { type: "warmup" };
      const response = await fetch(`${env.VITE_AGENT_API_URL}/chat`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Amzn-Bedrock-AgentCore-Runtime-Session-Id": sessionId,
        },
        body: JSON.stringify(body),
        keepalive: true,
        signal: controller.signal,
      });
      if (
        response.ok &&
        !controller.signal.aborted &&
        pendingWarmups.get(sessionId) === controller
      ) {
        lastWarmedAt.set(sessionId, Date.now());
      }
    } catch {
      /* swallow — a failed warm-up just means the first message is cold */
    } finally {
      clearTimeout(timeout);
      if (pendingWarmups.get(sessionId) === controller) pendingWarmups.delete(sessionId);
    }
  })();
};

export { parseSseFrames } from "./sse-parser";
